import { useMemo, useState } from 'react'

import { PageState } from '../app/PublicLayout.jsx'
import { thumbUrl, useCollection } from '../app/useSiteData.js'
import { useI18n } from '../lib/i18n.jsx'
import {
  Avatar,
  Chip,
  Container,
  EmptyState,
  ExternalLink,
  PageHead,
  Row,
  RowItem,
  RowList,
  Section,
  SectionHead,
} from '../ui/index.js'

/**
 * People page.
 *
 * Three populations that want three different densities, and forcing them into
 * one grid is the usual mistake. Faculty are few and each carries real
 * metadata — title, interests, four links — so they get wide horizontal rows
 * with a square portrait. The square is deliberate: a circular crop on a
 * faculty photo reads as a social-network avatar, and this page is a
 * directory. Students are many and carry a name and a cohort, so they get a
 * portrait grid where the photo edge *is* the card — no border, no hover
 * overlay, nothing to click through to. Alumni get that same card, with the
 * cohort line replaced by graduation year and destination.
 *
 * Alumni had a text list and no photo, on the argument that name, year and
 * destination are what someone scans an alumni list for. That is true of a long
 * list; it was not true of this one, where four alumni rendered as four bare
 * names. Two different treatments for "student" and "former student" also has to
 * earn its keep, and it did not.
 *
 * A group with nobody in one of the three is shown the other two. An empty
 * section shell is worse than no section.
 *
 * 成员页。
 *
 * 三类人群需要三种密度，把它们塞进同一个栅格是常见的错误。教师人数少且每人都带
 * 真正的元信息——职称、研究兴趣、四个链接——因此用横向宽行加方形照片。方形是刻意的：
 * 教师照片做圆形裁切会读作社交网络头像，而这一页是通讯录。学生人数多、只带姓名和
 * 年级，因此用照片栅格，照片的边缘「就是」卡片——没有边框、没有 hover 浮层、
 * 没有可点进去的东西。校友用同一张卡片，只是把年级那一行换成毕业年份与去向。
 *
 * 校友原先是没有照片的文字列表，理由是「姓名、年份、去向才是有人翻校友名单时要找的信息」。
 * 对一份很长的名单这是对的，对这一份不对：四位校友渲染出来就是四个光秃秃的名字。
 * 何况「学生」和「以前的学生」用两套不同的呈现方式，这件事本身也得对得起它的成本，
 * 而它没有。
 *
 * 三类中缺了一类的课题组只会看到另外两类。空的区块外壳比没有这个区块更糟。
 */

/** Degree buckets, in the order a directory lists them. 学位分组，按通讯录惯例排序。 */
const STUDENT_GROUPS = [
  { degree: 'phd', labelKey: 'members.phd' },
  { degree: 'master', labelKey: 'members.master' },
  { degree: 'undergrad', labelKey: 'members.undergrad' },
]

/** Filter chips across the top. 顶部筛选片。 */
const FILTERS = [
  { key: 'all', labelKey: 'common.all' },
  { key: 'faculty', labelKey: 'members.faculty' },
  { key: 'student', labelKey: 'members.students' },
  { key: 'alumni', labelKey: 'members.alumni' },
]

/**
 * Every spelling of every member's name, for citation bolding.
 *
 * Exported for the publications page, which bolds group members inside author
 * lists. Both language forms are collected because a Chinese paper cites 饶智博
 * and an English one cites Zhibo Rao — the same person, two strings, and the
 * list must bold whichever the citation used.
 *
 * 每位成员姓名的所有写法，供引用加粗使用。
 *
 * 导出给论文页：它要在作者串里加粗组内成员。两种语言形式都要收集，因为中文论文写
 * 「饶智博」而英文论文写 "Zhibo Rao"——同一个人、两个字符串，列表必须加粗引用里
 * 实际用到的那一个。
 *
 * @param {object[]} members Member records / 成员记录。
 * @returns {string[]} Names in both languages / 两种语言的姓名。
 */
export function collectMemberNames(members) {
  const names = []
  for (const member of members || []) {
    const name = member.name || {}
    if (typeof name === 'string') {
      names.push(name)
      continue
    }
    if (name.zh) names.push(name.zh)
    if (name.en) names.push(name.en)
  }
  return names.filter(Boolean)
}

/**
 * External profile links shared by the faculty row and the student card.
 *
 * 教师行与学生卡共用的个人链接组。
 *
 * @param {object} props Props / 属性。
 * @param {object} props.record Member record / 成员记录。
 * @returns {JSX.Element|null} Link row, or null when the member listed none / 链接行；一个都没填则为 null。
 */
function MemberLinks({ record }) {
  const { t } = useI18n()
  const links = [
    record.homepage && { href: record.homepage, label: t('members.homepage') },
    record.scholar && { href: record.scholar, label: t('members.scholar') },
    record.github && { href: record.github, label: t('members.github') },
  ].filter(Boolean)

  if (!links.length && !record.email) return null

  return (
    <Row className="member-links" gap="tight">
      {record.email && (
        <a className="member-links__item" href={`mailto:${record.email}`}>
          {t('members.email')}
        </a>
      )}
      {links.map((link) => (
        <ExternalLink key={link.href} className="member-links__item" href={link.href}>
          {link.label}
        </ExternalLink>
      ))}
    </Row>
  )
}

/**
 * Faculty entry — a wide row with a square portrait.
 *
 * 教师条目 —— 带方形照片的横向宽行。
 *
 * @param {object} props Props / 属性。
 * @param {object} props.record Member record / 成员记录。
 * @returns {JSX.Element} Row / 列表行。
 */
function FacultyRow({ record }) {
  const { pick } = useI18n()
  const name = pick(record.name)
  const interests = pick(record.interests)
  const bio = pick(record.bio)

  return (
    <RowItem
      className="member-row"
      meta={
        <Avatar
          className="member-row__portrait"
          src={thumbUrl(record.avatar)}
          name={name}
          size="lg"
          shape="square"
        />
      }
    >
      <h3 className="member-row__name">{name}</h3>
      {pick(record.role) && <p className="member-row__role">{pick(record.role)}</p>}
      {Array.isArray(interests) && interests.length > 0 && (
        <p className="member-row__interests">{interests.join(' · ')}</p>
      )}
      {bio && <p className="member-row__bio">{bio}</p>}
      <MemberLinks record={record} />
    </RowItem>
  )
}

/**
 * Student entry — portrait, name, cohort. Nothing else.
 *
 * 学生条目 —— 照片、姓名、年级，仅此而已。
 *
 * @param {object} props Props / 属性。
 * @param {object} props.record Member record / 成员记录。
 * @returns {JSX.Element} Card / 卡片。
 */
function StudentCard({ record }) {
  const { pick } = useI18n()
  const name = pick(record.name)

  return (
    <li className="member-card">
      <Avatar
        className="member-card__portrait"
        src={thumbUrl(record.avatar)}
        name={name}
        size="lg"
        shape="square"
      />
      <p className="member-card__name">{name}</p>
      {pick(record.role) && <p className="member-card__role">{pick(record.role)}</p>}
    </li>
  )
}

/**
 * Alumni entry — portrait, name, year, destination.
 *
 * The same card as a current student, because an alumnus *is* a former student and
 * inventing a second visual language for the same kind of person is how a directory
 * starts looking assembled rather than designed.
 *
 * This page used to give alumni a text list and no photo, on the argument that name,
 * year and destination are what someone scans an alumni list for. That argument holds
 * for a long list at a large group. It does not hold here: with four alumni the section
 * was four bare names, and a face is the thing a visitor actually recognises — these are
 * people the reader may have met. The photos already existed on every record; only the
 * page declined to show them.
 *
 * 校友条目 —— 照片、姓名、年份、去向。
 *
 * 用和在读学生完全相同的卡片，因为校友「就是」以前的学生；为同一类人另造一套视觉语言，
 * 正是通讯录从「设计过」滑向「拼凑而成」的起点。
 *
 * 这一页原先给校友的是没有照片的文字列表，理由是「姓名、年份、去向才是有人翻校友名单时
 * 要找的信息」。对一个人数众多的大组、一份很长的名单，这个理由成立；在这里不成立：
 * 四位校友时，那一栏就是四个光秃秃的名字，而人脸才是访客真正能认出来的东西
 * ——这些是读者可能见过面的人。照片在每条记录里本来就有，只是页面不肯显示。
 *
 * @param {object} props Props / 属性。
 * @param {object} props.record Member record / 成员记录。
 * @returns {JSX.Element} Card / 卡片。
 */
function AlumnusCard({ record }) {
  const { t, pick } = useI18n()
  const name = pick(record.name)
  const destination = pick(record.destination)

  return (
    <li className="member-card">
      <Avatar
        className="member-card__portrait"
        src={thumbUrl(record.avatar)}
        name={name}
        size="lg"
        shape="square"
      />
      <p className="member-card__name">{name}</p>
      {record.graduationYear && (
        <p className="member-card__role">{t('members.graduatedIn', { year: record.graduationYear })}</p>
      )}
      {/* Only rendered once someone has filled it in. An empty "去向：" label is a
          question the page asks and cannot answer, repeated once per alumnus.
          填了才显示。一个空的「去向：」标签，是页面自己提出却答不上来的问题，
          而且每位校友都要问一遍。 */}
      {destination && (
        <p className="member-card__destination">
          <span className="member-card__label">{t('members.destination')}</span>
          {destination}
        </p>
      )}
    </li>
  )
}

/**
 * People page.
 *
 * 成员页。
 *
 * @returns {JSX.Element} Page / 页面。
 */
export default function Members() {
  const { t } = useI18n()
  const { data: records, loading, error, reload } = useCollection('members')
  const [filter, setFilter] = useState('all')

  const groups = useMemo(() => {
    const all = records || []
    const current = all.filter((record) => record.memberStatus !== 'alumni')
    const students = current.filter((record) => record.kind !== 'faculty')
    const known = new Set(STUDENT_GROUPS.map((group) => group.degree))

    return {
      faculty: current.filter((record) => record.kind === 'faculty'),
      // A student whose degree track was never filled in still belongs on the
      // page; they fall into a final untitled-by-degree bucket rather than
      // disappearing.
      // 没填学位阶段的学生依然属于这一页；他们进入最后一个「未按学位划分」的分组，
      // 而不是凭空消失。
      students: [
        ...STUDENT_GROUPS.map((group) => ({
          key: group.degree,
          labelKey: group.labelKey,
          items: students.filter((record) => record.degree === group.degree),
        })),
        {
          key: 'other',
          labelKey: 'members.students',
          items: students.filter((record) => !known.has(record.degree)),
        },
      ].filter((group) => group.items.length > 0),
      alumni: all.filter((record) => record.memberStatus === 'alumni'),
    }
  }, [records])

  const showFaculty = (filter === 'all' || filter === 'faculty') && groups.faculty.length > 0
  const showStudents = (filter === 'all' || filter === 'student') && groups.students.length > 0
  const showAlumni = (filter === 'all' || filter === 'alumni') && groups.alumni.length > 0

  return (
    <Section className="members-page">
      <Container width="max">
        <PageHead
          title={t('members.title')}
          lede={t('members.lede')}
          actions={
            <Row className="members-filters" gap="tight">
              {FILTERS.map((item) => (
                <Chip
                  key={item.key}
                  className="members-filters__chip"
                  active={filter === item.key}
                  onClick={() => setFilter(item.key)}
                >
                  {t(item.labelKey)}
                </Chip>
              ))}
            </Row>
          }
        />

        <PageState
          loading={loading}
          error={error}
          onRetry={reload}
          isEmpty={(records || []).length === 0}
          empty={<EmptyState title={t('members.empty')} body={t('members.emptyBody')} />}
        >
          {showFaculty && (
            <section className="members-group">
              <SectionHead
                title={t('members.faculty')}
                aside={<span className="members-group__count">{t('common.count', { n: groups.faculty.length })}</span>}
              />
              <RowList className="member-rows">
                {groups.faculty.map((record) => (
                  <FacultyRow key={record.id} record={record} />
                ))}
              </RowList>
            </section>
          )}

          {showStudents &&
            groups.students.map((group) => (
              <section className="members-group" key={group.key}>
                <SectionHead
                  title={t(group.labelKey)}
                  aside={<span className="members-group__count">{t('common.count', { n: group.items.length })}</span>}
                />
                {/* A plain <ul> rather than <Grid>: a directory is a list, and
                    the column rule is a design decision the theme makes on
                    .member-grid.
                    用普通 <ul> 而不是 <Grid>：通讯录本质是列表，分栏规则是设计决策，
                    由主题在 .member-grid 上决定。 */}
                <ul className="member-grid">
                  {group.items.map((record) => (
                    <StudentCard key={record.id} record={record} />
                  ))}
                </ul>
              </section>
            ))}

          {showAlumni && (
            <section className="members-group">
              <SectionHead
                title={t('members.alumni')}
                aside={<span className="members-group__count">{t('common.count', { n: groups.alumni.length })}</span>}
              />
              <ul className="member-grid">
                {groups.alumni.map((record) => (
                  <AlumnusCard key={record.id} record={record} />
                ))}
              </ul>
            </section>
          )}
        </PageState>
      </Container>
    </Section>
  )
}
